import HideImageIcon from '@mui/icons-material/HideImage';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { storyblokEditable } from '@storyblok/react';
import { object } from 'prop-types';
import React, { memo, useCallback, useState } from 'react';

import useScreenSize from '../../../util/hooks/useScreenSize';
import useStoryblokImage from '../../../util/hooks/useStoryblokImage';
import ProgressiveImage from '../../Image/ProgressiveImage';
import Link from '../../Links/Link/Link';
import { withBaseComponent } from '../hoc/withBaseComponent';

const isExternalLink = (href) => href.linktype === 'url';

const storyRouteObject = (rawUrl) => {
  const cleanHref = (rawUrl || '')
    .replace(/^\/?(de|en|fr|it|es|sv|no)\//, '')
    .replace(/^\//, '');
  const [slug, ...restParts] = cleanHref.split('/');
  return {
    name: 'StoryblokPage',
    params: { slug, rest: restParts.join('/') },
  };
};

const linkProps = (href) => {
  if (isExternalLink(href)) {
    return {
      href: href.url, // Use `href` for external links
      target: '_blank',
      rel: 'noopener noreferrer',
    };
  } else if (href.linktype === 'story') {
    return {
      to: storyRouteObject(href.cached_url || href.url),
    };
  } else {
    return {
      to: href.cached_url || href.url,
    };
  }
};

const isValidHttpUrl = (url) => {
  try {
    new URL(url);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch (_) {
    return false;
  }
};

/**
 * @param {object} props
 * @param {object} props.blok
 * @param {object} props.baseStyles
 */
const StoryblokImageComponent = ({ blok, baseStyles }) => {
  const { general = { asset: {}, width: '100%', height: '100%', href: '' }, extra = { id: '' } } =
    blok;
  const { id } = extra;
  const { asset = { filename: null }, width, height } = general;

  const { filename, title, alt, copyright, source: assetSource } = asset;
  const hasSourceOrCopyright = !!copyright || !!assetSource;
  const { sources, blurSources, src, blurSrc } = useStoryblokImage(filename);
  const { alignSelf, ...restBaseStyles } = baseStyles;

  const { isMobile, isTablet, isDesktop } = useScreenSize();
  const heightForImage = useCallback(() => {
    return isMobile ? height.mobile : isTablet ? height.tablet : height.desktop;
  }, [isMobile, height.mobile, isTablet, height.tablet, isDesktop, height.desktop]);
  const widthForImage = useCallback(() => {
    return isMobile ? width.mobile : isTablet ? width.tablet : width.desktop;
  }, [isMobile, width.mobile, isTablet, width.tablet, isDesktop, width.desktop]);

  const useNaturalSize = !(heightForImage() || widthForImage());
  const [naturalSize, setNaturalSize] = useState();

  if (!asset) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'grey.300',
        }}
      >
        <HideImageIcon />
      </Box>
    );
  }

  const imageContent = (
    <ProgressiveImage
      addHelmet={true}
      alt={alt}
      blurSources={blurSources}
      blurSrc={blurSrc}
      height={heightForImage() || '100%'}
      imageStyle={{
        borderRadius: '8px',
      }}
      naturalSize={naturalSize}
      setNaturalSize={setNaturalSize}
      sources={sources}
      src={src}
      title={title}
      useNaturalSize={useNaturalSize}
      width={widthForImage() || '100%'}
    />
  );

  return (
    <Box id={id} sx={{ ...restBaseStyles, display: 'flex', flexDirection: 'column' }}>
      <Box
        {...storyblokEditable(blok)}
        sx={{
          alignSelf: alignSelf,
        }}
      >
        <Box
          sx={{
            width: widthForImage() || '100%',
            height: heightForImage() || '100%',
            maxWidth: '100%',
            maxHeight: '100%',
          }}
        >
          {!blok.href || (blok.href.url === '' && blok.href.id === '') ? (
            imageContent
          ) : (
            <Link {...linkProps(blok.href)}>{imageContent}</Link>
          )}
        </Box>
        {hasSourceOrCopyright ? (
          <Box sx={{ mb: 1 }}>
            <Typography sx={{ mt: 1 }} variant="caption">
              {copyright}
              {assetSource && copyright ? ` | ` : null}
              {isValidHttpUrl(assetSource) ? (
                <a href={assetSource} rel="nofollow noreferrer" target="_blank">
                  Link
                </a>
              ) : null}
            </Typography>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

StoryblokImageComponent.displayName = 'StoryblokImage';
const StoryblokImage = memo(StoryblokImageComponent);
export default memo(withBaseComponent(StoryblokImage));
